import {
  AddRounded,
  RemoveRounded,
  ToggleOffRounded,
  ToggleOnRounded,
} from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  createFilterOptions,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import AlertDialog from "components/AlertDialog";
import { Controller } from "react-hook-form";
import {
  CompatibilityDto,
  CustomerEnvironment,
  PackageGroupLabel,
  PackageVersion,
  ResourceType,
  isWorkerHostingEnvironment,
} from "utils/saastypes";
import { isValidSemVer } from "utils/utils";
import {
  isVersionWithinAllowedRange,
  PARAMETERS_KEY,
  StyledPopper,
  upgradeResourceTypes,
} from "./common";
import { useUpgradeDeploymentDialog } from "./useUpgradeDeploymentDialog";

// Extended PackageVersion interface to include disabled state
interface ExtendedPackageVersion extends PackageVersion {
  disabled?: boolean;
}

const filter = createFilterOptions<ExtendedPackageVersion>();

const UpgradeDeploymentDialog = ({
  clusterName,
  open,
  closeDialog,
  clusterDetail,
  showError,
  showSuccess,
  refetchLogs,
  compatibilityVersionList,
}: {
  clusterName?: string;
  open: boolean;
  closeDialog: () => void;
  clusterDetail: CustomerEnvironment;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  refetchLogs: () => void;
  compatibilityVersionList: CompatibilityDto[];
}) => {
  const [
    {
      selectedResourceType,
      packages,
      selectedResource,
      formErrors,
      isAcknowledged,
      parametersFields,
      isConfirmDisabled,
      shouldShowToggle,
      isOrkesUser,
      currentVersion,
    },
    {
      control,
      removeParameter,
      appendParameter,
      handleAcknowledged,
      handleConfirm,
    },
  ] = useUpgradeDeploymentDialog(
    clusterDetail,
    compatibilityVersionList,
    showError,
    showSuccess,
    refetchLogs,
    closeDialog,
    clusterName
  );

  return (
    <AlertDialog
      textTitle={"Upgrade Deployment"}
      textContent={``}
      open={open}
      setOpen={() => closeDialog()}
      isConfirmDisabled={isConfirmDisabled}
      handleConfirm={handleConfirm}
    >
      Upgrade the cluster <strong>{clusterDetail?.name}</strong> to the
      following Conductor version:
      <form>
        <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
          <Box style={{ width: "100%" }}>
            <FormControl fullWidth>
              <InputLabel id="resource-type-label">Resource Type</InputLabel>
              <Controller
                control={control}
                name="resource"
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="resource-type-label"
                    id="resource-type-select"
                    label="Resource Type"
                    onChange={(event: any) => {
                      field.onChange(event);
                      removeParameter(); //remove key-value parametersFields
                    }}
                  >
                    {upgradeResourceTypes.map((x: any) => {
                      const isWorkerHosting =
                        isWorkerHostingEnvironment(clusterDetail);
                      const isServerCluster =
                        (clusterDetail?.hostedWorkerEnvironments?.length ?? 0) >
                        0;
                      // Types that include Workers — hidden from server clusters
                      const workerTypes = [
                        ResourceType.WORKERS,
                        ResourceType.BOTH_CONDUCTOR_AND_WORKERS,
                        ResourceType.CONDUCTOR_SERVER_WORKERS_AND_METRICS_PROXY,
                      ];
                      if (
                        // worker hosting cluster: only Workers + Agent
                        (isWorkerHosting &&
                          ![ResourceType.WORKERS, ResourceType.AGENT].includes(
                            x.value
                          )) ||
                        // server cluster: hide all worker-related options
                        (isServerCluster && workerTypes.includes(x.value)) ||
                        // hide metrics proxy if not installed
                        (clusterDetail?.appVersions?.metricsProxy ===
                          undefined &&
                          [
                            ResourceType.METRICS_PROXY,
                            ResourceType.CONDUCTOR_SERVER_WORKERS_AND_METRICS_PROXY,
                          ].includes(x.value)) ||
                        // hide mcp workers if not deployed
                        (clusterDetail?.appVersions?.mcpWorker === undefined &&
                          x.value === ResourceType.MCP_WORKER)
                      ) {
                        return null;
                      }
                      return (
                        <MenuItem key={x.value} value={x.value}>
                          {x.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
                rules={{ required: true }}
              />
            </FormControl>

            <FormControl
              style={{
                marginTop: 16,
                marginBottom: 16,
              }}
              fullWidth
            >
              <Controller
                control={control}
                rules={{ required: true }}
                name="deploymentVersion"
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    id="conductor-version"
                    fullWidth
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    freeSolo
                    groupBy={(option) => option.group}
                    PopperComponent={StyledPopper}
                    onChange={(__, newValue: any) => {
                      // Prevent selection of disabled options
                      if (newValue && newValue.disabled) {
                        return;
                      }

                      if (typeof newValue === "string") {
                        // Check if custom string input is allowed
                        // Allow custom versions (non-semver) to bypass range checks
                        // If current version is custom, allow all semantic versions
                        const isCurrentVersionCustom =
                          !isValidSemVer(currentVersion);
                        if (
                          !isCurrentVersionCustom &&
                          !isVersionWithinAllowedRange(
                            newValue,
                            currentVersion
                          ) &&
                          isValidSemVer(newValue)
                        ) {
                          return;
                        }
                        field.onChange({
                          group: PackageGroupLabel.MAJOR,
                          title: newValue,
                          value: newValue,
                        });
                      } else {
                        field.onChange(newValue);
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;

                      // Show all options but mark invalid ones
                      // Allow custom versions (non-semver) to not be disabled
                      // If current version is custom (non-semver), allow all semantic versions
                      const isCurrentVersionCustom =
                        !isValidSemVer(currentVersion);
                      const allOptions: ExtendedPackageVersion[] = filtered.map(
                        (option) => ({
                          ...option,
                          disabled:
                            !isCurrentVersionCustom &&
                            !isVersionWithinAllowedRange(
                              option.value,
                              currentVersion
                            ) &&
                            isValidSemVer(option.value),
                        })
                      );

                      // Suggest the creation of a new value
                      const isExisting = options.some(
                        (option) => inputValue === option.title
                      );

                      if (inputValue !== "" && !isExisting && isOrkesUser) {
                        const isCustomVersionAllowed =
                          isVersionWithinAllowedRange(
                            inputValue,
                            currentVersion
                          );
                        // Allow custom versions (non-semver) to not be disabled
                        // If current version is custom, allow all semantic versions
                        const shouldDisable =
                          !isCurrentVersionCustom &&
                          !isCustomVersionAllowed &&
                          isValidSemVer(inputValue);
                        allOptions.push({
                          group: PackageGroupLabel.MAJOR,
                          value: inputValue,
                          title: inputValue,
                          disabled: shouldDisable,
                        } as ExtendedPackageVersion);
                      }

                      return allOptions;
                    }}
                    options={packages}
                    getOptionLabel={(option) => {
                      // Value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }

                      // Regular option
                      return option.title;
                    }}
                    renderOption={(props, option: ExtendedPackageVersion) => {
                      const isDisabled = option.disabled;
                      return (
                        <li
                          {...props}
                          style={{
                            ...props.style,
                            opacity: isDisabled ? 0.5 : 1,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            pointerEvents: isDisabled ? "none" : "auto",
                          }}
                          title={
                            isDisabled
                              ? `Cannot upgrade more than 1 major version above current version (${currentVersion})`
                              : undefined
                          }
                        >
                          {option.title}
                          {isDisabled && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{
                                marginLeft: 1,
                                color: "text.secondary",
                                fontStyle: "italic",
                              }}
                            >
                              {`(Upgrade not allowed: > 1 Major version)`}
                            </Typography>
                          )}
                        </li>
                      );
                    }}
                    renderInput={(params) => {
                      const hasError =
                        !isAcknowledged && !!formErrors["deploymentVersion"];
                      const errorMessage =
                        formErrors["deploymentVersion"]?.message;
                      const versionRestrictionText = currentVersion
                        ? `Can only upgrade up to 1 major version above current (${currentVersion}) or use a custom version`
                        : "";

                      return (
                        <TextField
                          {...params}
                          label={`${selectedResourceType.name} version`}
                          error={hasError}
                          helperText={
                            hasError ? errorMessage : versionRestrictionText
                          }
                        />
                      );
                    }}
                  />
                )}
              />
            </FormControl>

            {shouldShowToggle && (
              <Box>
                <Typography
                  component="i"
                  style={{ marginTop: 0, marginBottom: 0 }}
                >
                  Agree to upgrade
                </Typography>
                <IconButton onClick={handleAcknowledged}>
                  {isAcknowledged ? (
                    <ToggleOnRounded
                      style={{ fontSize: 48, color: "#1976d2" }}
                    />
                  ) : (
                    <ToggleOffRounded
                      style={{
                        fontSize: 48,
                        stroke: "#1976d2",
                        color: "transparent",
                      }}
                    />
                  )}
                </IconButton>
              </Box>
            )}

            <Typography component="p" style={{ marginTop: 0, marginBottom: 0 }}>
              Key-Value parameters:
            </Typography>
            <Box>
              {[
                ResourceType.BOTH_CONDUCTOR_AND_WORKERS,
                ResourceType.CONDUCTOR_SERVER_WORKERS_AND_METRICS_PROXY,
              ].includes(selectedResource) ? (
                <Typography component="i">
                  Adding parameters is not permitted while upgrading multiple
                  resources. Please perform parameter adjustments separately for
                  each resource upgrade.
                </Typography>
              ) : (
                <Box>
                  <Typography component="i">
                    Leaving the value empty will remove the application property
                    if it exists
                  </Typography>
                  {
                    <Grid container spacing={2} sx={{ marginTop: 0.2 }}>
                      {parametersFields.map((item, index) => {
                        const parameterKey = `${PARAMETERS_KEY}[${index}].key`;
                        const parameterValue = `${PARAMETERS_KEY}[${index}].value`;
                        const errorMessage =
                          formErrors && formErrors[PARAMETERS_KEY]
                            ? (formErrors[PARAMETERS_KEY] as any)?.[index]
                            : null;

                        return (
                          <Grid item xs={12} key={item.id}>
                            <Grid container spacing={2}>
                              <Grid item md={5.5}>
                                <FormControl fullWidth>
                                  <Controller
                                    control={control}
                                    // @ts-ignore
                                    name={parameterKey}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        label="Key"
                                        placeholder="Key"
                                        required
                                        error={!!errorMessage?.key?.message}
                                        helperText={errorMessage?.key?.message}
                                      />
                                    )}
                                    rules={{ required: true }}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item md={5.5}>
                                <FormControl fullWidth>
                                  <Controller
                                    control={control}
                                    // @ts-ignore
                                    name={parameterValue}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        label="Value"
                                        placeholder="Value"
                                        error={!!errorMessage?.value?.message}
                                        helperText={
                                          errorMessage?.value?.message
                                        }
                                      />
                                    )}
                                    rules={{ required: false }}
                                  />
                                </FormControl>
                              </Grid>
                              <Grid item xs={1}>
                                <IconButton
                                  sx={{ marginTop: 1 }}
                                  onClick={() => removeParameter(index)}
                                >
                                  <RemoveRounded />
                                </IconButton>
                              </Grid>
                            </Grid>
                          </Grid>
                        );
                      })}

                      <Grid item xs={2}>
                        <IconButton
                          onClick={() =>
                            appendParameter({ key: "", value: "" })
                          }
                        >
                          <AddRounded />
                        </IconButton>
                      </Grid>
                    </Grid>
                  }
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </form>
    </AlertDialog>
  );
};

export default UpgradeDeploymentDialog;
